import Button from '@atoms/Button/Button';
import { IContentOverlay } from '@models/IContentOverlay';
import { IStatCard } from '@models/IFeature';
import { ILink } from '@models/ILink';
import { IMeta } from '@models/IMeta';
import ContentOverlay from '@molecules/ContentOverlay/ContentOverlay';
import updateDataLayer from '@utils/helpers/gtm';
import classNames from 'classnames';

interface Props {
  readonly contentOverlayToolTip?: IContentOverlay;
  readonly meta: IMeta;
  readonly preCTA?: string;
  readonly primaryCTA: ILink;
  readonly text: string;
  readonly title: string;
  readonly categorySpringboard?: boolean;
  readonly overlapItem?: boolean;
  readonly dataTestId?: string;
  readonly statCards?: IStatCard[];
  readonly fullWidth?: boolean;
}

const FeatureTextContent = ({
  title,
  text,
  categorySpringboard = false,
  contentOverlayToolTip,
  preCTA,
  primaryCTA,
  meta,
  overlapItem,
  dataTestId = 'Feature-Text-Content',
  statCards,
  fullWidth,
}: Props) => {
  const isH1Style = overlapItem || !categorySpringboard ? '' : 'h1';

  return (
    <div className="xl:mx-auto xl:text-left">
      {title && (
        <p
          className={classNames('h3 mb-5 xl:mb-7.5', isH1Style)}
          data-epi-property-name={meta?.editHints?.title}
          data-testid={`${dataTestId}-Title`}
        >
          {title}
        </p>
      )}

      {contentOverlayToolTip && (
        <div className="mb-5 xl:mb-6">
          <ContentOverlay
            {...contentOverlayToolTip}
            dataTestId={dataTestId}
            className="body-four"
          />
        </div>
      )}

      {text && (
        <div
          className="body-two text-black-100 content-area dark:text-grey-500"
          data-epi-property-name={meta?.editHints?.text}
          dangerouslySetInnerHTML={{ __html: text }}
          data-testid={`${dataTestId}-Text`}
        />
      )}

      {preCTA && (
        <div
          className="body-one mt-2.5 text-grey-450 content-area dark:text-grey-500"
          data-epi-property-name={meta?.editHints?.preCTA}
          dangerouslySetInnerHTML={{ __html: preCTA }}
          data-testid={`${dataTestId}-Pre-Cta`}
        />
      )}

      {primaryCTA?.url && primaryCTA?.name && (
        <Button
          link={primaryCTA}
          icon={primaryCTA?.linkIcon}
          className="mt-5"
          linkCallback={() => {
            updateDataLayer({
              event: 'selectContent',
              content_type: 'feature_content',
              link_text: primaryCTA.name,
            });
          }}
          dataTestId={`${dataTestId}-Cta`}
        />
      )}

      {!!statCards?.length && (
        <div
          className={classNames(
            'flex flex-col gap-5 max-xl:pt-7.5 md:flex-row xl:pt-[70px]',
            fullWidth ? 'xl:w-[120%]' : 'xl:w-[110%]'
          )}
          data-epi-property-name={meta?.editHints?.statCards}
        >
          {statCards.map((statCard) => (
            <div
              className={classNames(
                'flex flex-col items-start gap-[5px] rounded-[10px] bg-grey-100 p-7.5 text-black-100 dark:bg-grey-600 dark:text-white',
                statCards.length === 1 ? 'max-md:w-full md:w-3/5' : 'w-full'
              )}
              key={statCard.title}
              data-testid={`${dataTestId}-Stat-Card`}
            >
              <p className="h5" data-testid={`${dataTestId}-Stat-Card-Title`}>
                {statCard.title}
              </p>
              <p
                className="body-two"
                data-testid={`${dataTestId}-Stat-Card-Text`}
              >
                {statCard.text}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FeatureTextContent;
